import { ReactComponent as LeftArrow } from "../../../../assets/icons/leftArrow.svg";
import { ReactComponent as RightArrow } from "../../../../assets/icons/rightArrow.svg";
import { ReactComponent as ArrowLeft } from "../../../../assets/icons/arrowLeft.svg";
import { ReactComponent as File } from "../../../../assets/icons/file.svg";
import { ReactComponent as SelectIcon } from "../../../../../common/assets/icons/selectIcon.svg";
import { usePostOrderReceiptDownload } from "../../../../../common/hooks/console/transaction/usePostOrderReceiptDownload";

import Input from "../../../../components/common/input";
import SalesDetailTable from "./SalesDetailTable";
import SalesInfo from "./SalesInfo";
import React, { useEffect, useState } from "react";
import { useGetSalesCustomerList } from "../../../../../common/hooks/console/sales/useGetSalesCustomer";
import { useGetSalesCustomerCount } from "../../../../../common/hooks/console/sales/useGetSalesCustomerCount";
import { useGetSalesInfo } from "../../../../../common/hooks/console/sales/useGetSalesInfo";
import { useGetSalesDeliveryDate } from "../../../../../common/hooks/console/sales/useGetDeliveryDate";
import { useNavigate } from "react-router-dom";
import SelectBox from "../../../../components/common/selectBox";
import { useOpenedWindowsStore } from "../../../../store/useOpenedWindowsStore";
import Excel from "../../../../components/common/excel";
import Button from "../../../../../common/components/button";

type SalesDetailProps = {
    showSalesDetail: () => void;
    customerIdx?: number;
    customer: string;
};

const SalesDetail = ({ showSalesDetail, customerIdx = 0, customer }: SalesDetailProps) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState<string>(customer === "개인 회원" ? "order_name" : "all_search");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [inputValue, setInputValue] = useState<string>(currentPage?.toString() || "");
    const [searchTxt, setSearchTxt] = useState<string>("");
    const [field, setField] = useState<string>("delivery_date");
    const [direction, setDirection] = useState<"ASC" | "DESC">("DESC");
    const [orderIdx, setOrderIdx] = useState<number>(0);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [status, setStatus] = useState<number[]>([]);
    const [excelClick, setExcelClick] = useState(false);
    const [selectedOrders, setSelectedOrders] = useState<number[]>([]);

    const handleDateChange = (start?: Date, end?: Date) => {
        if (start) setStartDate(start);
        if (end) setEndDate(end);
    };

    const { data: deliveryDateData } = useGetSalesDeliveryDate({
        customerIdx,
        payment_status: status.length === 1 ? status[0] : undefined,
    });
    const { data: listData } = useGetSalesCustomerList(customerIdx, {
        page: currentPage,
        search: search,
        searchtxt: searchTxt,
        field: field,
        direction: direction,
        startDate: startDate,
        endDate: endDate,
        status:
            status.length === 2 && status.includes(1) && status.includes(2)
                ? 0
                : status.length === 1 && status.includes(1)
                    ? 1
                    : status.length === 1 && status.includes(2)
                        ? 2
                        : 0,
    });

    const { data: countData, refetch: refetchCount } = useGetSalesCustomerCount(customerIdx, startDate, endDate);
    const { data: infoData, refetch } = useGetSalesInfo(
        customerIdx,
        orderIdx,
        startDate,
        endDate,
        search,
        searchTxt,
        selectedOrders,
    );

    const { mutate: downloadReceipt } = usePostOrderReceiptDownload(undefined, error => {
        const message = error?.response?.data?.message || "조회 기간은 최대 12개월까지만 가능합니다.";
        alert(message);
    });

    const handleNavigate = () => {
        const { openWindow } = useOpenedWindowsStore.getState();

        if (customerIdx) {
            const path = "/console/settings";
            const id = "settings";

            localStorage.setItem("customer_idx", customerIdx.toString());
            openWindow(id, path);
        }
    };

    const handleStatusToggle = (value: number) => {
        setStatus(prevStatus => (prevStatus.includes(value) ? [] : [value]));
        setCurrentPage(1);
        setInputValue("1");
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setInputValue(page.toString());
    };

    const handlePageInput = (value: string) => {
        if (value.trim() === "") {
            setInputValue("");
            return;
        }
        if (!/^\d*$/.test(value)) {
            return;
        }
        const pageNumber = Number(value.trim());
        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > (listData?.last_page || 1)) {
            alert("페이지 범위를 벗어났습니다.");
            setInputValue(value.slice(0, -1));
            return;
        }

        setInputValue(value);
        handlePageChange(pageNumber);
    };

    const handleSort = (newField: string) => {
        setField(prevField => {
            if (prevField === newField) {
                setDirection(prevDirection => (prevDirection === "DESC" ? "ASC" : "DESC"));
                return prevField;
            } else {
                setDirection("ASC");
                return newField;
            }
        });
    };

    const handleReceiptDownload = () => {
        if (selectedOrders.length === 0) {
            alert("선택된 주문이 없습니다.");
            return;
        }

        downloadReceipt({
            order_idx: selectedOrders,
            menu: "sales",
            order_status: [1, 2, 3, 4, 5, 6],
            payment_status: [1, 2],
            branch_type: [1, 2],
            delivery_name: [],
            customer_idx: [],
            customer_branch_idx: [],
            start_date: startDate?.toISOString().split("T")[0],
            start_date_time: "00:00:00",
            end_date: endDate?.toISOString().split("T")[0],
            end_date_time: "23:59:59",
            is_excel_date: true,
        });
    };

    useEffect(() => {
        // 미수/입금완료 필터(status) 토글 시 deliveryDateData가 해당 필터의 주문기간으로 갱신되므로
        // 조회 기간을 그 기간에 맞춘다. (최초 로드 포함)
        // deliveryDateData의 oldest/latest 값은 필터가 바뀔 때만 변하고 사용자가 기간을 직접 수정해도
        // 변하지 않으므로, 이 값들에만 의존시키면 수동 변경한 기간은 덮어쓰지 않는다.
        setStartDate(deliveryDateData?.oldest_delivery_date ? new Date(deliveryDateData.oldest_delivery_date) : undefined);
        setEndDate(deliveryDateData?.latest_delivery_date ? new Date(deliveryDateData.latest_delivery_date) : undefined);
    }, [deliveryDateData?.oldest_delivery_date, deliveryDateData?.latest_delivery_date]);

    useEffect(() => {
        if (startDate && endDate) {
            refetch();
            refetchCount();
        }
    }, [startDate, endDate, refetch, refetchCount]);

    return (
        <div className="flex w-full">
            <div className="h-full flex w-full flex-col overflow-hidden">
                {/* Header */}
                <div className="flex justify-between p-3 items-center border-b border-gray4">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                navigate("/console/sales");
                            }}
                            className="border mr-2 border-gray4 rounded-lg w-[34px] h-[34px] flex items-center justify-center"
                        >
                            <ArrowLeft />
                        </button>
                        <p className="text-2xl">{infoData?.customer_name}</p>
                        <File />
                        <button onClick={handleNavigate} className="text-sm underline">
                            고객 관리
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => currentPage && currentPage > 1 && handlePageChange(currentPage - 1)}>
                            <LeftArrow />
                        </button>
                        <Input
                            inputClassName="text-center"
                            className="w-20 text-center outline-0 text-sm flex"
                            value={inputValue}
                            onChange={e => handlePageInput(e.target.value)}
                        />
                        <p className="text-gray3">/ {listData?.last_page}</p>
                        <button
                            onClick={() => currentPage && currentPage < (listData?.last_page || 1) && handlePageChange(currentPage + 1)}
                        >
                            <RightArrow />
                        </button>
                    </div>
                </div>
                {/* Title */}
                <div className=" p-3 text-sm items-center j border-b border-gray4 xl:flex">
                    <p className="font-bold mr-4 w-56">
                        {startDate ? startDate.toLocaleDateString() : "날짜 선택"} ~ {endDate ? endDate.toLocaleDateString() : "날짜 선택"}
                    </p>

                    <div className="flex items-center w-full">
                        <div className="mt-2 xl:mt-0 flex gap-x-4 items-center flex-wrap">
                            <div className="flex items-center gap-1">
                                <p className="text-xs">누적 주문건수</p>
                                <button className="text-sm font-bold text-primary2">
                                    {countData?.total_order_count.toLocaleString()} 건
                                </button>
                            </div>
                            <div className="flex items-center gap-1">
                                <p className="text-xs">누적 판매금액</p>
                                <button className="font-bold text-primary2">{countData?.total_sales_amount.toLocaleString()} 원</button>
                            </div>
                            <div className="w-1 h-1 rounded-full bg-gray4" />

                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold mr-2">입금완료</p>
                                <p className="text-xs">총</p>
                                <button
                                    className={`text-sm text-skyblue underline ${status.includes(2) ? "font-bold" : ""}`}
                                    onClick={() => handleStatusToggle(2)}
                                >
                                    {countData?.payment_complete_count.toLocaleString()} 건
                                </button>
                            </div>
                            <div className="w-1 h-1 rounded-full bg-gray4" />
                            <div className="flex gap-1 items-center">
                                <p className="text-sm font-bold mr-2">미수</p>
                                <p className="text-xs">총</p>
                                <button
                                    className={`text-sm text-error underline ${status.includes(1) ? "font-bold" : ""}`}
                                    onClick={() => handleStatusToggle(1)}
                                >
                                    {countData?.pending_count.toLocaleString()} 건
                                </button>
                            </div>
                        </div>
                        <div className="flex w-[240px] ml-auto gap-1">
                            <SelectBox
                                status="none"
                                className="min-w-[120px]"
                                options={
                                    customer === "개인 회원"
                                        ? [
                                              { value: "order_name", label: "주문하신분" },
                                              { value: "m_name", label: "회원명" },
                                              { value: "order_number", label: "배송일자명" },
                                          ]
                                        : [
                                              { value: "all_search", label: "전체검색" },
                                              { value: "customer_name", label: "고객명" },
                                              { value: "order_name", label: "주문자" },
                                              { value: "recipient_name", label: "받으시는 분" },
                                              { value: "branch_name", label: "타지점명" },
                                              { value: "delivery_address", label: "배송주소" },
                                              { value: "order_number", label: "배송일자명" },
                                              { value: "manager_name", label: "담당자" },
                                              { value: "product_name", label: "상품명" },
                                          ]
                                }
                                value={search}
                                onChange={(value: string) => setSearch(value)}
                            />
                            <Input
                                value={searchTxt}
                                onChange={e => setSearchTxt(e.target.value)}
                                placeholder="검색"
                                status="none"
                                className="w-full h-8"
                            />
                        </div>
                        <div className="ml-1 flex gap-2">
                            <Button onClick={handleReceiptDownload} size="S" className="px-2">
                                체크박스 청구서 다운로드
                            </Button>
                            <Button
                                onClick={() => {
                                    setExcelClick(true);
                                }}
                                size="S"
                                className="px-2"
                                icon={<SelectIcon className={`${excelClick ? "rotate-180" : ""}`} />}
                                iconPosition="right"
                            >
                                청구서
                            </Button>
                            {excelClick && (
                                <Excel
                                    initialDownloadClick="receipt"
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    onDateChange={handleDateChange}
                                    customer={infoData?.customer_name}
                                    customer_idx={infoData?.customer_idx}
                                    onlyReceipt
                                    onClose={() => setExcelClick(false)}
                                />
                            )}
                        </div>
                    </div>
                </div>
                <div className="overflow-y-auto">
                    <SalesDetailTable
                        customerIdx={customerIdx}
                        onSort={handleSort}
                        data={listData?.data_list}
                        onSelectOrders={setSelectedOrders}
                    />
                </div>
            </div>
            {customer === "개인 회원" || (
                <div className="md:min-w-[620px] overflow-y-auto min-w-[560px] bg-gray6 p-4 h-dvh">
                    <SalesInfo
                        customer={customer}
                        customerIdx={customerIdx}
                        orderIdx={orderIdx}
                        startDate={startDate}
                        endDate={endDate}
                        onDateChange={handleDateChange}
                        pendingAmount={infoData?.pending_amount}
                        oldestDeliveryDate={deliveryDateData?.oldest_delivery_date || ""}
                        latestDeliveryDate={deliveryDateData?.latest_delivery_date || ""}
                        selectedOrders={selectedOrders}
                        search={search}
                        searchTxt={searchTxt}
                    />
                </div>
            )}
        </div>
    );
};

export default SalesDetail;
